import { useMemo, useCallback, useState, useEffect, useRef, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFilterStore } from "@/lib/store/filterStore";
import { useQuery } from "@tanstack/react-query";
import { DocTypeListPageShell } from "@/features/common/components/DocTypeListPageShell";
import { useItemListWithVariants } from "./api/inventoryHooks";
import type { Item, ItemWithVariants } from "@/lib/erp";
import { usePageTranslation } from "@/i18n/useTranslation";
import { useLanguage } from "@/i18n/useLanguage";
import { inventoryTranslations } from "@/i18n/locales/en/inventory";
import { inventoryTranslations as thInventoryTranslations } from "@/i18n/locales/th/inventory";
import { getListViewSchema } from "@/lib/views/listViewSchemas";
import type { ListSettingsFieldConfig } from "@/lib/hooks/listSettingsTypes";
import type { AdvancedTableLabels } from "@/components/advanced-table/advancedTableLabels";
import type { DataGridLabels } from "@/components/data-grid/dataGridLabels";
import {
  getActiveListViewConfig,
  buildColumnsFromListViewConfig,
  convertListViewSortToTanStackSort,
} from "@/lib/views/utils/buildListColumnsFromSchema";
import { StorefrontToggleCell } from "./components/StorefrontToggleCell";
import { InlineDetailPanel } from "./components/inline-detail/InlineDetailPanel";
import type { SubRowColumnInfo } from "@/features/common/components/ListTableBody";
import { frappeMethod } from "@/lib/erp/utils";
import { Heart, MessageCircle, Clock, Users, ArrowRight } from "lucide-react";
import type { SystemColumnToggle } from "@/features/common/components/ListSettingsModal";

const SYSCOL_STORAGE_KEY = "zvg-hidden-system-columns";

function readHiddenSystemCols(doctype: string): string[] {
  try {
    const raw = localStorage.getItem(SYSCOL_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.[doctype]) ? parsed[doctype] : [];
  } catch { return []; }
}

function writeHiddenSystemCols(doctype: string, ids: string[]) {
  try {
    const raw = localStorage.getItem(SYSCOL_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[doctype] = ids;
    localStorage.setItem(SYSCOL_STORAGE_KEY, JSON.stringify(parsed));
  } catch { /* ignore */ }
}

export function ItemListPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = usePageTranslation({
    en: inventoryTranslations,
    th: thInventoryTranslations,
  });

  // --- Inline detail panel state ---
  const [detailPanelItemCode, setDetailPanelItemCode] = useState<string | null>(null);
  const handlePanelClose = useCallback(() => setDetailPanelItemCode(null), []);

  // Auto-close panel when URL params change (filter/sort/pagination/search)
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    if (detailPanelItemCode) setDetailPanelItemCode(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsKey]);

  // Auto-close panel when Zustand filter store changes (non-URL filters)
  const filters = useFilterStore(
    useCallback((s) => s.scopedFilters["inventory.items"], []),
  );
  useEffect(() => {
    if (!mountedRef.current) return;
    if (detailPanelItemCode) setDetailPanelItemCode(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // Focus management: restore focus to row on panel close
  const lastOpenedItemRef = useRef<string | null>(null);
  useEffect(() => {
    if (detailPanelItemCode) {
      lastOpenedItemRef.current = detailPanelItemCode;
    } else if (lastOpenedItemRef.current) {
      const rowEl = document.querySelector(
        `[data-row-id="${CSS.escape(lastOpenedItemRef.current)}"]`,
      ) as HTMLElement | null;
      rowEl?.focus();
      lastOpenedItemRef.current = null;
    }
  }, [detailPanelItemCode]);

  const handleRowClick = useCallback((row: ItemWithVariants) => {
    const itemCode = row.item_code ?? row.name;
    setDetailPanelItemCode((prev) => (prev === itemCode ? null : itemCode));
  }, []);

  const renderSubRow = useCallback(
    (row: ItemWithVariants, columns: SubRowColumnInfo): ReactNode | null => {
      const itemCode = row.item_code ?? row.name;
      if (itemCode !== detailPanelItemCode) return null;
      return (
        <InlineDetailPanel
          key={itemCode}
          itemCode={itemCode}
          itemName={row.item_name}
          detailPath={`/inventory/item/${row.name}`}
          onClose={handlePanelClose}
          columns={columns}
        />
      );
    },
    [detailPanelItemCode],
  );

  // --- Advanced Table: i18n labels ---
  const advancedTableLabels = useMemo<Partial<AdvancedTableLabels>>(
    () => ({
      filterWhere: t.advFilterWhere,
      filterAnd: t.advFilterAnd,
      filterAddFilter: t.advFilterAddFilter,
      filterClearAll: t.advFilterClearAll,
      filterNoFilters: t.advFilterNoFilters,
      filterSelectField: t.advFilterSelectField,
      filterSearchFields: t.advFilterSearchFields,
      filterNoFields: t.advFilterNoFields,
      filterOperator: t.advFilterOperator,
      filterValue: t.advFilterValue,
      filterSelect: t.advFilterSelect,
      filterYes: t.advFilterYes,
      filterNo: t.advFilterNo,
      filterMin: t.advFilterMin,
      filterMax: t.advFilterMax,
      operatorLabels: {
        like: t.advOpContains,
        equals: t.advOpIs,
        notEquals: t.advOpIsNot,
        notLike: t.advOpDoesNotContain,
        isSet: t.advOpIsNotEmpty,
        isNotSet: t.advOpIsEmpty,
        greaterThan: t.advOpGreaterThan,
        lessThan: t.advOpLessThan,
        greaterOrEqual: t.advOpGreaterOrEqual,
        lessOrEqual: t.advOpLessOrEqual,
        between: t.advOpBetween,
        in: t.advOpIsAnyOf,
        notIn: t.advOpIsNoneOf,
      },
      sort: t.advSort,
      sortRules: t.advSortRules,
      sortAdd: t.advSortAdd,
      sortClearAll: t.advSortClearAll,
      sortNoRules: t.advSortNoRules,
      sortSelectField: t.advSortSelectField,
      sortAscending: t.advSortAscending,
      sortDescending: t.advSortDescending,
      actionSelected: t.advActionSelected,
      actionSetStatus: t.advActionSetStatus,
      actionEnable: t.advActionEnable,
      actionDisable: t.advActionDisable,
      actionExportCSV: t.advActionExportCSV,
      actionArchive: t.advActionArchive,
      actionCancel: t.advActionCancel,
      actionArchiveTitle: t.advActionArchiveTitle,
      actionArchiveDescription: t.advActionArchiveDescription,
      actionArchiveConfirm: t.advActionArchiveConfirm,
      toastEnabled: t.advToastEnabled,
      toastDisabled: t.advToastDisabled,
      toastArchived: t.advToastArchived,
      toastFailedUpdate: t.advToastFailedUpdate,
      toastFailedArchive: t.advToastFailedArchive,
      toastPartialFail: t.advToastPartialFail,
      emptyTitle: t.advEmptyTitle,
      emptyDescription: t.advEmptyDescription,
      columns: t.advColumns,
      columnsSearch: t.advColumnsSearch,
      columnsEmpty: t.advColumnsEmpty,
      filters: t.advFilters,
    }),
    [t],
  );

  // --- Advanced Table: column overrides (StorefrontToggleCell) ---
  const advancedColumnOverrides = useMemo(
    () => ({
      storefront_enabled: ({ row }: { row: { getValue: (k: string) => unknown; original: ItemWithVariants } }) => {
        const value = row.getValue("storefront_enabled");
        const checked = value === 1 || value === true || value === "1";
        return (
          <div className="hidden md:block text-center">
            <StorefrontToggleCell
              itemName={row.original.name}
              storefrontEnabled={checked}
            />
          </div>
        );
      },
    }),
    [],
  );

  // --- Advanced Table: fetch Item Group options for filter dropdown ---
  const { data: itemGroupOptions } = useQuery({
    queryKey: ["Item Group", "select-options"],
    queryFn: () =>
      frappeMethod<{ name: string }[]>("frappe.client.get_list", {
        doctype: "Item Group",
        fields: ["name"],
        order_by: "name asc",
        limit_page_length: 0,
      }),
    staleTime: 5 * 60 * 1000,
    select: (data) =>
      data?.map((d) => ({ label: d.name, value: d.name })) ?? [],
  });

  const advancedFilterOptions = useMemo(
    () => (itemGroupOptions ? { item_group: itemGroupOptions } : undefined),
    [itemGroupOptions],
  );

  // Data grid i18n labels
  const advancedDataGridLabels = useMemo<Partial<DataGridLabels>>(
    () => ({
      dgEditBanner: t.dgEditBanner,
      dgToggleEditMode: t.dgToggleEditMode,
      dgCopy: t.dgCopy,
      dgPaste: t.dgPaste,
      dgClearCell: t.dgClearCell,
      dgViewDetail: t.dgViewDetail,
      dgPasteTitle: t.dgPasteTitle,
      dgPasteDescription: t.dgPasteDescription,
      dgPasteCurrent: t.dgPasteCurrent,
      dgPasteNew: t.dgPasteNew,
      dgPasteMore: t.dgPasteMore,
      dgPasteSaving: t.dgPasteSaving,
      dgPasteApply: t.dgPasteApply,
      dgPasteSavingBtn: t.dgPasteSavingBtn,
      dgCancel: t.dgCancel,
      dgConflictTooltip: t.dgConflictTooltip,
      dgKeyboardHelp: t.dgKeyboardHelp,
      dgNavigation: t.dgNavigation,
      dgEditing: t.dgEditing,
      dgClipboard: t.dgClipboard,
      dgUndoRedo: t.dgUndoRedo,
      dgShortcutTab: t.dgShortcutTab,
      dgShortcutEnter: t.dgShortcutEnter,
      dgShortcutEscape: t.dgShortcutEscape,
      dgShortcutArrows: t.dgShortcutArrows,
      dgShortcutHome: t.dgShortcutHome,
      dgShortcutEnd: t.dgShortcutEnd,
      dgShortcutCtrlHome: t.dgShortcutCtrlHome,
      dgShortcutCtrlEnd: t.dgShortcutCtrlEnd,
      dgShortcutPageUp: t.dgShortcutPageUp,
      dgShortcutPageDown: t.dgShortcutPageDown,
      dgShortcutCtrlZ: t.dgShortcutCtrlZ,
      dgShortcutCtrlShiftZ: t.dgShortcutCtrlShiftZ,
      dgShortcutCtrlC: t.dgShortcutCtrlC,
      dgShortcutCtrlV: t.dgShortcutCtrlV,
      dgShortcutCtrlX: t.dgShortcutCtrlX,
      dgShortcutQuestion: t.dgShortcutQuestion,
    }),
    [t],
  );

  // Data grid editable fields — defines which columns support inline editing
  const advancedEditableFields = useMemo(
    () => new Set([
      'item_name',
      'standard_rate',
      'item_group',
      'is_stock_item',
      'storefront_enabled',
    ]),
    [],
  );

  // Suppress TS6133 — staged for future advanced table mode
  void advancedTableLabels; void advancedColumnOverrides; void advancedFilterOptions;
  void advancedDataGridLabels; void advancedEditableFields;

  // System columns visibility (persisted to localStorage, no Zustand)
  const [hiddenSystemCols, setHiddenSystemCols] = useState(() => readHiddenSystemCols("Item"));

  const systemColumnsConfig = useMemo(() => ({
    enabled: true,
    showAssignedTo: !hiddenSystemCols.includes("__assigned_to"),
    showCreated: !hiddenSystemCols.includes("__created"),
    showComments: !hiddenSystemCols.includes("__comments"),
    showLikes: !hiddenSystemCols.includes("__like"),
    assignedToField: "_assign",
    commentCountField: "comment_count",
    likedByField: "_liked_by",
    createdField: "creation",
  }), [hiddenSystemCols]);

  const systemColumnToggles = useMemo<SystemColumnToggle[]>(() => [
    { id: "__like", label: "Likes", icon: <Heart className="h-4 w-4" />, visible: !hiddenSystemCols.includes("__like") },
    { id: "__comments", label: "Comments", icon: <MessageCircle className="h-4 w-4" />, visible: !hiddenSystemCols.includes("__comments") },
    { id: "__created", label: "Created", icon: <Clock className="h-4 w-4" />, visible: !hiddenSystemCols.includes("__created") },
    { id: "__assigned_to", label: "Assigned To", icon: <Users className="h-4 w-4" />, visible: !hiddenSystemCols.includes("__assigned_to") },
  ], [hiddenSystemCols]);

  const handleSystemColumnToggle = useCallback((id: string, visible: boolean) => {
    setHiddenSystemCols((prev) => {
      const next = visible ? prev.filter((c) => c !== id) : [...prev, id];
      writeHiddenSystemCols("Item", next);
      return next;
    });
  }, []);

  // Get schema and view config
  const schema = getListViewSchema("Item");
  const viewConfig = getActiveListViewConfig(schema, "experience");

  // Build columns from schema
  // Note: Item has special variant expansion logic handled by getSubRows below
  const buildColumns = useMemo(
    () => {
      if (!viewConfig) {
        return () => [];
      }
      const schemaColumns = buildColumnsFromListViewConfig<ItemWithVariants>(viewConfig);
      return ({ 
        items: _items, 
        visibleFieldOrder, 
        availableFields 
      }: { 
        items: Item[] | ItemWithVariants[]; 
        visibleFieldOrder?: string[]; 
        availableFields?: ListSettingsFieldConfig[];
      }) => {
        // For v0, ignore visibleFieldOrder and availableFields - schema is source of truth
        // TODO: In future, we can merge schema columns with user-selected columns from list settings
        void _items;
        void visibleFieldOrder;
        void availableFields;

        // Override columns with custom cell renderers
        return schemaColumns.map((col) => {
          if (col.id === "item_name") {
            return {
              ...col,
              cell: ({ row, getValue }: { row: { original: ItemWithVariants }; getValue: () => unknown }) => {
                const value = getValue() as string | null;
                return (
                  <div className="group/name relative flex items-center w-full min-w-0">
                    <span className="truncate">{value || row.original.name}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/inventory/item/${row.original.name}`);
                      }}
                      className="absolute right-0 hidden group-hover/name:flex items-center gap-1 px-2 py-0.5 rounded-xs text-xs font-medium text-sky-600 dark:text-sky-400 bg-white/90 dark:bg-neutral-900/90 shadow-sm border border-neutral-200 dark:border-neutral-700 hover:bg-sky-50 dark:hover:bg-sky-950/50 transition-colors"
                      aria-label="View item details"
                    >
                      Details
                      <ArrowRight className="h-3 w-3" />
                    </button>
                  </div>
                );
              },
            };
          }
          if (col.id === "storefront_enabled") {
            return {
              ...col,
              cell: ({ row }: { row: { getValue: (key: string) => unknown; original: ItemWithVariants } }) => {
                const value = row.getValue("storefront_enabled");
                const checked = value === 1 || value === true || value === "1";
                return (
                  <div className="hidden md:block text-center">
                    <StorefrontToggleCell
                      itemName={row.original.name}
                      storefrontEnabled={checked}
                    />
                  </div>
                );
              },
            };
          }
          return col;
        });
      };
    },
    [viewConfig]
  );

  // Get default sort from schema
  const defaultSorting = useMemo(() => {
    if (!viewConfig?.defaultSort) {
      return [{ id: "modified", desc: true }];
    }
    return convertListViewSortToTanStackSort(viewConfig.defaultSort);
  }, [viewConfig]);

  return (
    <div key={language}>
      <div>
        <DocTypeListPageShell<ItemWithVariants>
          doctype="Item"
          columns={buildColumns}
          filterStoreKey="inventory.items"
          useListHook={useItemListWithVariants}
          defaultSorting={defaultSorting}
          pageSizeOptions={viewConfig?.pageSizeOptions ?? [20, 50, 100, 200, 400, 500]}
          searchPlaceholder={t.searchPlaceholder}
          title={t.items}
          description={t.manageInventoryItems}
          newButtonLabel={t.newItem}
          onNewClick={() => navigate("/inventory/item/new")}
          onRowClick={handleRowClick}
          getRowId={(row) => row.name}
          renderSubRow={renderSubRow}
          activeSubRowId={detailPanelItemCode ?? undefined}
          getSubRows={(row) => (row as ItemWithVariants).variants}
          defaultExpanded={(row) => !!(row as ItemWithVariants).has_variants}
          thumbnailConfig={{
            imageFieldnames: ["primary_image", "image"],
            labelFieldnames: ["item_name", "item_code", "name"],
            sizePx: 48,
          }}
          enableViewModeSwitch={true}
          galleryConfig={{
            sizePx: 160,
            titleFieldnames: ["item_name", "item_code"],
            subtitleFieldnames: ["item_group"],
          }}
          className="pt-2"
          fullWidth
          listSettingsConfig={{
            key: "inventory.items",
            meta: {
              doctype: "Item",
              includeFieldtypes: [
                "Data",
                "Int",
                "Float",
                "Currency",
                "Check",
                "Date",
                "Datetime",
                "Link",
                "Select",
                "Small Text",
                "Text",
                "Long Text",
              ],
              excludeFieldtypes: [
                "Section Break",
                "Column Break",
                "HTML",
                "Table",
                "Table MultiSelect",
                "Button",
                "Heading",
              ],
              excludeFieldnames: [
                "naming_series",
                "owner",
                "creation",          // System column
                "modified",
                "modified_by",
                "idx",
                "__islocal",
                "__unsaved",
                "docstatus",
                "amended_from",
                "amended_date",
                "_assign",           // System column
                "_comment_count",    // System column
                "_liked_by",         // System column
              ],
              respectInListView: true,
              requiredFieldnames: ["item_code", "item_name"],
              // Schema default: use schema columns as initial visible order
              defaultVisibleFieldnames:
                viewConfig?.columns
                  ?.map((col) => col.fieldname)
                  .filter((v): v is string => typeof v === "string" && v.length > 0) || [],
              strictMode: false, // v1: disabled to show all meta fields, not just those with existing columns
              // columnIds not passed - allows all safe meta fields to appear in List Settings
            },
            staticFields: [
              {
                fieldname: "__thumbnail",
                label: "Thumbnails",
                defaultVisible: true,
              },
            ],
          }}
          systemColumnsConfig={systemColumnsConfig}
          systemColumnToggles={systemColumnToggles}
          onSystemColumnToggle={handleSystemColumnToggle}
        />
      </div>
    </div>
  );
}

